import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { NotificationBell } from "@/components/NotificationBell";
import { WinnerShoutout } from "@/components/WinnerShoutout";
import { useAppSettings } from "@/lib/appSettings";

const NOTICE_STYLES = {
  info: "bg-slate-900 text-white",
  warning: "bg-amber-100 text-amber-900 border-b border-amber-300",
  critical: "bg-red-600 text-white",
};

const NoticeBanner = () => {
  const s = useAppSettings();
  if (!s.notice_enabled || !s.notice_text) { return null; }
  return (
    <div data-testid="notice-banner" className={`px-4 md:px-8 py-2 text-xs md:text-sm whitespace-pre-wrap break-words ${NOTICE_STYLES[s.notice_level] || NOTICE_STYLES.info}`}>
      {s.notice_text}
    </div>
  );
};
import { LayoutDashboard, TicketIcon, BookOpen, Settings as SettingsIcon, LogOut, Plus, UserCircle, Trash2, Kanban, Users, Menu, X, Trophy } from "lucide-react";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { useTheme, ThemeSwitcher } from "@/lib/theme";
import { useEffect, useState } from "react";

const navItems = [
  { to: "/dashboard", key: "nav_dashboard", icon: LayoutDashboard, roles: ["end_user", "agent", "team_leader", "admin", "manager"] },
  { to: "/tickets", key: "nav_tickets", icon: TicketIcon, roles: ["end_user", "agent", "team_leader", "admin", "manager"] },
  { to: "/board", key: "nav_board", icon: Kanban, roles: ["agent", "team_leader", "admin", "manager"] },
  { to: "/customers", key: "nav_customers", icon: Users, roles: ["agent", "team_leader", "admin", "manager"] },
  { to: "/kb", key: "nav_kb", icon: BookOpen, roles: ["end_user", "agent", "team_leader", "admin", "manager"] },
  { to: "/hall-of-fame", key: "nav_hof", icon: Trophy, roles: ["agent", "team_leader", "admin", "manager"] },
  { to: "/settings", key: "nav_profile", icon: UserCircle, roles: ["end_user", "agent", "team_leader", "admin", "manager"] },
  { to: "/trash", key: "nav_trash", icon: Trash2, roles: ["admin"] },
  { to: "/admin", key: "nav_admin", icon: SettingsIcon, roles: ["admin"] },
];

const ROLE_STYLES = {
  admin:        "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  team_leader:  "bg-red-500/15 text-red-700 border-red-500/40",
  manager:      "bg-purple-500/15 text-purple-300 border-purple-500/40",
  agent:        "bg-red-500/15 text-red-700 border-red-500/40",
  end_user:     "bg-slate-500/15 text-slate-300 border-slate-500/40",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const { theme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Close the drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  if (!user) return null;

  const visibleNav = navItems.filter((i) => i.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-white text-slate-900 flex">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          data-testid="mobile-backdrop"
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`w-60 border-r border-slate-200 flex flex-col shrink-0 bg-gradient-to-b from-slate-50 to-white
          fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 md:static md:translate-x-0
          ${mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-white">
          <img src="/kinos-logo.jpg" alt="KINOS" className="h-16 md:h-24 w-auto object-contain rounded-sm bg-white p-1" data-testid="brand-logo-sidebar" />
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setMobileOpen(false)}
            className="md:hidden text-slate-500 hover:text-slate-900 p-1.5"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>

        <nav className="flex-1 py-4">
          <div className="px-5 mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{t("nav_workspace")}</div>
          {visibleNav.map(({ to, key, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.replace("/", "")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 mx-2 my-0.5 px-3 py-2.5 rounded-md text-[13px] transition-all ${
                  isActive
                    ? "bg-red-600 text-white shadow-sm shadow-red-600/25"
                    : "text-slate-700 hover:text-slate-900 hover:bg-white hover:shadow-sm"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              <span className="font-medium tracking-tight">{t(key)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <Link to="/tickets/new">
            <Button data-testid="sidebar-new-ticket-btn" className="w-full bg-red-600 hover:bg-red-500 text-white rounded-sm">
              <Plus className="h-4 w-4" strokeWidth={2} />
              {t("new_ticket")}
            </Button>
          </Link>
        </div>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 p-2">
            <Avatar className="h-8 w-8 rounded-sm">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="rounded-sm bg-slate-100 text-slate-700 text-xs">{(user.name || user.email).slice(0,2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate text-slate-900" data-testid="user-name">{user.name}</div>
              <div className="mt-1">
                <span
                  data-testid="user-role"
                  className={`inline-block text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 border rounded-sm ${ROLE_STYLES[user.role] || ROLE_STYLES.end_user}`}
                >
                  {user.role.replace("_", " ")}
                </span>
              </div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-500 hover:text-slate-900 p-1.5">
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
          <div className="px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-2">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-slate-700 hover:text-slate-900 p-1.5 border border-slate-200 rounded-sm"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.25em] text-slate-500 truncate flex-1" data-testid="page-crumb">
              {location.pathname.split("/").filter(Boolean).join(" / ") || "dashboard"}
            </div>
            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              {user.role !== "end_user" && <NotificationBell />}
              <ThemeSwitcher />
              <LanguageSwitcher compact />
              <span
                data-testid="header-role-pill"
                className={`hidden sm:inline-block text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 border rounded-sm ${ROLE_STYLES[user.role] || ROLE_STYLES.end_user}`}
              >
                {user.role === "end_user" ? "END USER" : user.role.replace("_", " ")}
              </span>
              <div className="hidden lg:block text-xs text-slate-500 font-mono">{new Date().toDateString()}</div>
            </div>
          </div>
          {user.role === "end_user" && (
            <div className="px-4 md:px-8 pb-3 -mt-1" data-testid="end-user-hint">
              <div className="text-[11px] font-mono text-slate-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">
                You&apos;re signed in as an <b className="text-slate-900">END USER</b>. Board, Trash, Admin, delete icons, split composer and bulk-assign are only available to <b className="text-slate-900">staff / admin</b> accounts.
                <button onClick={logout} className="ml-2 underline text-red-600 hover:text-red-700" data-testid="end-user-hint-signout">Sign out to switch account</button>
              </div>
            </div>
          )}
          <NoticeBanner />
        </header>
        <WinnerShoutout />
        <div className="flex-1 p-4 md:p-8 lg:p-10 bg-slate-50 text-slate-900 workspace-shell" data-theme={theme}>
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
}
